import { Component, Input, OnChanges, OnInit, AfterViewInit } from '@angular/core';
import { ListItemRendererComponent } from 'gung-list';
import { OrderStatistic, TopProductsStatistics } from '../../../models/order-statistics';
import { ProductService } from '../../../services/products/product.service';
import { TopProductsActionButtonConfigService } from '../../../services/top-products-action-button-config/top-products-action-button-config.service';

export interface TopProductsTotals {
  products: number;
  quantity: number;
  orders: number;
  customers: number;
}

@Component({
    selector: 'lib-top-products-summary',
    templateUrl: './top-products-summary.component.html',
    styleUrls: ['./top-products-summary.component.scss'],
    standalone: false
})
export class TopProductsSummaryComponent
  extends ListItemRendererComponent<OrderStatistic[]>
  implements OnInit, OnChanges, AfterViewInit
{
  @Input() items: undefined; // temporary

  public changes = 0;
  public expandedNumber = -1;

  public productsInfo = {};

  public _items: OrderStatistic[] = [];
  public _productsSorted: TopProductsStatistics[] = [];

  public totals: TopProductsTotals = { products: 0, quantity: 0, orders: 0, customers: 0 };
  public highestQty = 0;
  public copiedKey: string = null;
  private copiedResetTimer: ReturnType<typeof setTimeout>;

  public loadingNumber = -1;
  public closingNumber = -1;
  public detailRowLimit = 0;
  // Enough rows to fill the scroll container, so the panel keeps its height when the real rows arrive
  public skeletonRows = Array.from({ length: 9 });
  protected detailRowStep = 100;
  protected openDelayMs = 300;
  protected closeDurationMs = 200;
  protected toggleLockMs = 400;
  private toggleLockedUntil = 0;

  constructor(
    protected productService: ProductService,
    public buttonConfigService: TopProductsActionButtonConfigService
  ) {
    super();
  }

  ngOnInit() {
    this.mapProductsBySeason();
  }

  ngAfterViewInit(): void {
    this.renderFinished?.emit();
  }

  ngOnChanges() {
    this.mapProductsBySeason();
  }

  updateSummary(_items: OrderStatistic[]) {
    this.changes++;
    delete this._items;
    this._items = [];
    this._items = _items;

    this.mapProductsBySeason();
  }

  expand(numberToExpand: number) {
    this.expandedNumber = numberToExpand;
  }

  toggle(item: TopProductsStatistics) {
    if (this.loadingNumber !== -1 || this.closingNumber !== -1 || Date.now() < this.toggleLockedUntil) {
      return;
    }
    if (item.number === this.expandedNumber) {
      this.closingNumber = item.number;
      setTimeout(() => {
        this.expand(-1);
        this.closingNumber = -1;
        this.lockToggle();
      }, this.closeDurationMs);
      return;
    }
    this.closingNumber = this.expandedNumber;
    this.loadingNumber = item.number;
    // The open animation runs on the cheap skeleton rows; the real rows render once it has finished
    setTimeout(() => {
      this.detailRowLimit = this.detailRowStep;
      this.expand(item.number);
      this.closingNumber = -1;
      this.loadingNumber = -1;
      this.lockToggle();
      setTimeout(() => this.lockToggle());
    }, this.openDelayMs);
  }

  // Swallows the second click of a double click, which would otherwise close the row again
  private lockToggle() {
    this.toggleLockedUntil = Date.now() + this.toggleLockMs;
  }

  onDetailsScroll(event: Event, item: TopProductsStatistics) {
    const container = event.target as HTMLElement;
    const nearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 200;
    if (nearBottom && this.detailRowLimit < (item.subItems?.length || 0)) {
      this.detailRowLimit += this.detailRowStep;
    }
  }

  public getGroupId(orderStatistic: OrderStatistic) {
    return orderStatistic.productModelId;
  }

  mapProductsBySeason() {
    const products = {};
    for (const orderStatistic of this.data) {
      if (!!orderStatistic.productModelId && orderStatistic.productModelId !== '-') {
        const groupId = this.getGroupId(orderStatistic);
        products[groupId] = products[groupId] || {
          qty: 0,
          orderCount: 0,
          productId: groupId,
          productName: orderStatistic.productName,
          subItems: []
        };

        products[groupId].orderCount += 1;
        products[groupId].qty += orderStatistic.orderRowTotalQuantity;
        products[groupId].subItems.push(orderStatistic);
      }
    }

    const displayProducts: TopProductsStatistics[] = [];
    const keys = Object.keys(products);
    keys.forEach(key => {
      displayProducts.push(products[key]);
    });

    const pIds = [];
    if (displayProducts.length > 0) {
      for (const p of displayProducts) {
        if (!this.productsInfo[p.productId]) {
          pIds.push(p.productId);
        }
      }
      if (pIds.length > 0) {
        this.productService.getFullProductsByIds(pIds).subscribe(fullProducts => {
          for (const product of fullProducts) {
            this.productsInfo[product.id] = product;
          }
        });
      }
    }
    displayProducts.sort((a, b) => {
      if (a.qty < b.qty) {
        return 1;
      }
      if (a.qty > b.qty) {
        return -1;
      }
      return 0;
    });
    let i = 1;
    displayProducts.forEach(p => {
      p.number = i++;
      p.subItems?.sort((a, b) => b.orderRowTotalQuantity - a.orderRowTotalQuantity);
    });
    this._productsSorted = displayProducts;
    this.updateTotals(displayProducts);
  }

  protected updateTotals(displayProducts: TopProductsStatistics[]) {
    const orderIds = new Set<string>();
    const customerIds = new Set<string>();
    let quantity = 0;
    for (const product of displayProducts) {
      quantity += product.qty;
      for (const subItem of product.subItems || []) {
        orderIds.add(subItem.orderId);
        customerIds.add(subItem.customerId);
      }
    }
    this.totals = { products: displayProducts.length, quantity, orders: orderIds.size, customers: customerIds.size };
    this.highestQty = displayProducts[0]?.qty || 0;
  }

  getShareOfQuantity(item: TopProductsStatistics): number {
    return this.totals.quantity > 0 ? item.qty / this.totals.quantity : 0;
  }

  getBarWidth(item: TopProductsStatistics): number {
    return this.highestQty > 0 ? Math.max(0, (item.qty / this.highestQty) * 100) : 0;
  }

  copyToClipboard(value: string, key: string) {
    if (!value || !navigator.clipboard) {
      return;
    }
    navigator.clipboard.writeText(value).then(() => {
      this.copiedKey = key;
      clearTimeout(this.copiedResetTimer);
      this.copiedResetTimer = setTimeout(() => (this.copiedKey = null), 1500);
    });
  }

  getImage(item: TopProductsStatistics) {
    return this.productsInfo[item.productId]?.extra?.images?.[0];
  }

  formatDate(date) {
    const d = new Date(date);
    let month = (d.getMonth() + 1).toString();
    let day = d.getDate().toString();
    const year = d.getFullYear();
    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }
    return [year, month, day].join('-');
  }
}
